import { calculateTicketedBookingTotal } from "utils/widgetBookingPricing"
import { countTickets } from "utils/ticketedBooking"
import { getBookableTicketTypes } from "pagesV2/BookingWidget/helpers"

// The widget's breakdown in the shape New Booking's summaries read (splitPriceBreakdown): a line per
// ticket type ("Adults x 4"), then one Taxes & Fees entry holding the fees the guide lists on their
// own line. Add-ons are left out; the summaries list them from the selection's addOns.
const CARD_OR_LINK_PAYMENTS = ["card", "link", "paypalLink"]

const toBookingBreakdown = (breakdown) => {
  const ticketLines = breakdown
    .filter((line) => line.kind === "ticket")
    .map(({ label, amount, value }) => ({ label, amount, rawAmount: value }))
  const listedFees = breakdown
    .filter((line) => line.kind === "fee")
    .map(({ label, amount, value }) => ({ label, amount, rawAmount: value, isLineItem: true }))
  const feesTotal = breakdown
    .filter((line) => line.kind === "fee" || line.kind === "tax_and_fees")
    .reduce((sum, line) => sum + line.value, 0)
  return feesTotal > 0 ? [...ticketLines, { isTaxFee: true, rawAmount: feesTotal, children: listedFees }] : ticketLines
}

/**
 * A ticketed selection's price in the fields New Booking keeps on bookingSelections, as
 * calculateBookingTotal's callers store a private one. There is no guide-side ticketed pricing yet
 * (guide-orders-backend.md, G6), so it is the guest widget's calculator, which matches the API for
 * widget bookings. Null until a ticket is chosen.
 *
 * @param {object} params
 * @param {object} params.experience       the experience detail (ticket_types, add_ons, fees, adjustments)
 * @param {object} params.selections       bookingSelections: date, ticketCounts, addOns
 * @param {Array}  params.discountPromos   applied promo codes
 * @param {number} params.depositFraction  share of the pre-card total paid by card now
 * @param {string} params.paymentType      the guide's payment method on step 2 (card, link, paypalLink, cash)
 */
export const priceTicketedSelection = ({ experience, selections, discountPromos = [], depositFraction = 1, paymentType }) => {
  const ticketTypes = getBookableTicketTypes(experience)
  if (!selections?.date || countTickets(ticketTypes, selections.ticketCounts) === 0) return null
  // The widget is always paid by card. A guide's booking carries the platform fee and the card fee
  // only when paid by card or link, as calculateBookingTotal prices a private one: cash has neither.
  const isCardOrLink = CARD_OR_LINK_PAYMENTS.includes(paymentType)
  const shared = {
    ticketTypes,
    ticketCounts: selections.ticketCounts,
    date: selections.date,
    addOnSelections: selections.addOns || {},
    tripAddOns: experience?.add_ons || [],
    customFees: (experience?.assigned_custom_fees || []).filter((fee) => isCardOrLink || fee?.title !== "Platform Fee"),
    discountPromos,
    cardFeePassthroughEnabled: isCardOrLink && experience?.platform_fee_setting?.card_fee_passthrough_enabled === true,
    depositFraction,
    isCardFeeListed: false
  }
  const adjusted = calculateTicketedBookingTotal({
    ...shared,
    dayAdjustments: experience?.day_adjustments,
    seasonalAdjustments: experience?.seasonal_adjustments,
    availableDateRanges: experience?.available_date_ranges
  })
  const original = calculateTicketedBookingTotal(shared)
  return {
    amount: adjusted.total,
    baseAmount: adjusted.guestSubtotal,
    originalBaseAmount: adjusted.guestSubtotal,
    breakdown: toBookingBreakdown(adjusted.breakdown),
    preCardTotal: adjusted.preCardTotal,
    preCardTotalBeforeAdjustments: original.preCardTotal,
    cardProcessingFee: adjusted.total - adjusted.preCardTotal
  }
}
